"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { FaAndroid, FaApple } from "react-icons/fa";
import { LuArrowLeft, LuCheck, LuDownload, LuEllipsis, LuPlus, LuShare } from "react-icons/lu";
import S from "@/app/install/page.module.scss";

type Platform = "iphone" | "android";

const STEP_ICONS = {
  iphone: [LuShare, LuPlus, LuCheck],
  android: [LuEllipsis, LuDownload, LuCheck],
} as const;

const InstallView = () => {
  const t = useTranslations("installPage");
  const router = useRouter();
  const [platform, setPlatform] = useState<Platform>("iphone");
  const steps = STEP_ICONS[platform];

  useEffect(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (isStandalone) router.replace("/estates");
  }, [router]);

  return (
    <main className={S.page}>
      <div className={S.grid}>
        <div className={S.content}>
          <div className={S.pageHeader}>
            <Link href="/estates" className={S.backLink} aria-label={t("back")}>
              <LuArrowLeft aria-hidden="true" />
            </Link>
            <span>{t("title")}</span>
          </div>

          <section className={S.intro} aria-labelledby="install-title">
            <p className={S.eyebrow}>{t("eyebrow")}</p>
            <h1 id="install-title" className={S.headline}>
              {t("headlineBefore")} <span>{t("headlineAccent")}</span>
              <br />
              {t("headlineAfter")}
            </h1>
            <p className={S.description}>{t("description")}</p>
          </section>

          <div className={S.platformTabs} role="group" aria-label={t("choosePlatform")}>
            <button
              type="button"
              className={platform === "iphone" ? S.platformActive : S.platformButton}
              aria-pressed={platform === "iphone"}
              onClick={() => setPlatform("iphone")}
            >
              <FaApple aria-hidden="true" /> iPhone
            </button>
            <button
              type="button"
              className={platform === "android" ? S.platformActive : S.platformButton}
              aria-pressed={platform === "android"}
              onClick={() => setPlatform("android")}
            >
              <FaAndroid aria-hidden="true" /> Android
            </button>
          </div>

          <section className={S.instructions} aria-label={t("stepsLabel")}>
            <ol className={S.steps}>
              {steps.map((Icon, index) => {
                const stepNumber = index + 1;
                return (
                  <li className={S.step} key={`${platform}-${stepNumber}`}>
                    <div className={S.stepIcon} aria-hidden="true"><Icon /></div>
                    <div className={S.stepCopy}>
                      <h2><span>{stepNumber}.</span> {t(`${platform}.step${stepNumber}Title`)}</h2>
                      <p>{t(`${platform}.step${stepNumber}Description`)}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>

          <p className={S.tip}>{t(`${platform}.tip`)}</p>
          <p className={S.onlineNote}>{t("onlineNote")}</p>
        </div>

        <aside className={S.preview} aria-label={t("previewLabel")}>
          <div className={S.phoneBack} aria-hidden="true">
            <div className={S.phoneNotch} />
            <div className={S.previewLines}><i /><i /><i /><i /></div>
          </div>
          <div className={S.phoneFront}>
            <div className={S.phoneNotch} aria-hidden="true" />
            <div className={S.phoneGlow} aria-hidden="true" />
            <div className={S.phoneContent}>
              <Image src="/icons/pwa-192.png" alt="" width={96} height={96} className={S.appIcon} />
              <strong>RESTATIC</strong>
              <span>{t("previewText")}</span>
            </div>
            <div className={S.homeIndicator} aria-hidden="true" />
          </div>
        </aside>
      </div>
    </main>
  );
};

export default InstallView;
